import { useState } from "react";
import { X, Check, Lock } from "lucide-react";
import { TIER_CONFIG } from "@/lib/tierGate";
import { apiPost } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const TIER_ORDER = ["FREE", "PLUS", "PRO", "ELITE"];

// Upgrade Wall Modal — Section 5. Comparison matrix across all 4 tiers + a dev/test
// "Simulate" button (admin-only server-side — see tier_service.py) that instantly
// flips the session's tier for QA, alongside the REAL Stripe upgrade path for everyone
// else via `onOpenRealUpgrade`.
export default function UpgradeWallModal({ open, onClose, currentTier, requiredTier, onOpenRealUpgrade, onTierChanged }) {
  const [simLoading, setSimLoading] = useState(null);
  if (!open) return null;

  const simulateUpgrade = async (tier) => {
    setSimLoading(tier);
    try {
      await apiPost("/user/tier/upgrade-sim", { tier });
      toast.success(`Simulated tier switched to ${tier}`);
      onTierChanged?.(tier);
    } catch (err) {
      if (err?.status === 403) {
        toast.error("Tier simulation is admin/dev-only — opening the real Upgrade flow.");
        onOpenRealUpgrade?.();
      } else {
        toast.error(err?.message || "Couldn't simulate that tier.");
      }
    } finally {
      setSimLoading(null);
    }
  };

  return (
    <div id="upgrade-modal" data-testid="upgrade-modal" className="fixed inset-0 z-[165] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-[#121214]">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black text-slate-900 dark:text-white">
            {requiredTier ? `Unlock more with ${requiredTier}` : "Choose Your Tier"}
          </h3>
          <button onClick={onClose} data-testid="upgrade-modal-close" aria-label="Close">
            <X className="h-5 w-5 text-slate-400 hover:text-slate-700 dark:hover:text-white" />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4" data-testid="upgrade-modal-matrix">
          {TIER_ORDER.map((tier) => {
            const cfg = TIER_CONFIG[tier];
            const isCurrent = tier === currentTier;
            return (
              <div key={tier} data-testid={`tier-card-${tier}`} className={`flex flex-col rounded-2xl border p-3 ${isCurrent ? "border-brand-orange bg-brand-yellow/10" : "border-slate-200 dark:border-white/10"}`}>
                <p className="text-sm font-black text-slate-900 dark:text-white">{tier}</p>
                <p className="mt-1 text-lg font-black text-brand-orange dark:text-amber-400">${cfg.priceMonthly.toFixed(2)}<span className="text-xs font-bold text-slate-400">/mo</span></p>
                <ul className="mt-2 flex-1 space-y-1 text-[11px] font-bold text-slate-500 dark:text-zinc-400">
                  <li>{cfg.maxSourcesShown === Infinity ? "Unlimited" : cfg.maxSourcesShown} deal source{cfg.maxSourcesShown === 1 ? "" : "s"} shown</li>
                  <li>{cfg.maxDailyDeckCards === Infinity ? "Unlimited" : cfg.maxDailyDeckCards} daily deck cards</li>
                  <li>{cfg.maxSavedVaultItems === Infinity ? "Unlimited" : cfg.maxSavedVaultItems} saved items</li>
                  <li>{cfg.exactLocationTax ? "Exact local sales tax" : "Estimated tax"}</li>
                  <li>{cfg.pdpReviewsUnlocked ? "Multi-source PDP reviews" : "PDP reviews locked"}</li>
                </ul>
                {isCurrent ? (
                  <span className="mt-3 inline-flex items-center justify-center gap-1 rounded-full bg-emerald-500/15 py-1.5 text-[10px] font-black uppercase text-emerald-600 dark:text-emerald-400">
                    <Check className="h-3 w-3" /> Current
                  </span>
                ) : (
                  <button
                    onClick={() => simulateUpgrade(tier)}
                    disabled={simLoading === tier}
                    data-testid={`tier-simulate-${tier}`}
                    className="mt-3 rounded-full bg-slate-900 py-1.5 text-[10px] font-black uppercase text-white disabled:opacity-50 dark:bg-white dark:text-slate-950"
                  >
                    {simLoading === tier ? "…" : "Simulate"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <button onClick={() => { onClose(); onOpenRealUpgrade?.(); }} data-testid="upgrade-modal-real-upgrade" className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-brand-yellow to-brand-orange py-3 text-xs font-black uppercase text-slate-950">
          <Lock className="h-4 w-4" /> Upgrade Tier
        </button>
      </div>
    </div>
  );
}
